import { useState, useEffect, useMemo } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { DEFAULT_AI_PROVIDER } from "../../constants/aiProviderCatalog";
import { getPrompt, validatePromptApi, updatePrompt, createPrompt } from "../../services/prompt.api";

// Definir initialState fuera del componente para evitar recreación
const initialState = {
    name: "",
    prompt: "",
    voice: "texto", // ✅ POR DEFECTO SIEMPRE "TEXTO"
    voiceKey: "", // ✅ COMENTADO EN UI PERO MANTENIDO INTERNAMENTE
    voiceRegion: "", // ✅ COMENTADO EN UI PERO MANTENIDO INTERNAMENTE
    maxTokens: 400, // ✅ OPTIMIZADO: De 100 a 400 (respuestas completas)
    temperature: 0.5, // ✅ OPTIMIZADO: De 1.0 a 0.5 (balanceado)
    apiKey: "",
    queueId: "", // ✅ CAMBIADO: De null a string vacío (OPCIONAL)
    maxMessages: 25, // ✅ OPTIMIZADO: De 10 a 25 (contexto adecuado)
    provider: DEFAULT_AI_PROVIDER,
    workMode: "layers" // ✅ NUEVO: Modo de trabajo (layers o pdfs)
};

// D8.3.4b — Estado y lógica del formulario del prompt (carga / validar API / guardar / cerrar).
// Recibe del media hook las funciones que necesita (inyección) para no acoplar ambos hooks.
export default function usePromptForm({
    promptId,
    open,
    onClose,
    onSave,
    fileInputRef,
    setMediaFiles,
    setUrlModalOpen,
    setIsDragging,
    fetchMediaFiles,
}) {
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState(DEFAULT_AI_PROVIDER);
    const [validatingApi, setValidatingApi] = useState(false);
    const [expandedPromptModalOpen, setExpandedPromptModalOpen] = useState(false);
    const [expandedPromptText, setExpandedPromptText] = useState("");
    const [expandedPromptChanged, setExpandedPromptChanged] = useState(false);
    const [apiValidated, setApiValidated] = useState(false);
    const [apiValidationError, setApiValidationError] = useState(null);
    /** GET detalle `/prompt/:id` envía apiKey literal (misma empresa) para edición / probar conexión §0290. */
    const [hasStoredApiKey, setHasStoredApiKey] = useState(false);
    const [savedProvider, setSavedProvider] = useState(null);
    const [selectedWorkMode, setSelectedWorkMode] = useState('layers'); // 'layers' o 'pdfs'
    const [prompt, setPrompt] = useState(initialState);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                name: Yup.string().required(i18n.t("promptModal.validation.nameRequired")),
                apiKey: Yup.string().test(
                    "api-required-or-masked-on-server",
                    i18n.t("promptModal.validation.apiKeyRequired"),
                    val => {
                        if (hasStoredApiKey && !(val && String(val).trim()))
                            return true;
                        return !!(val && String(val).trim());
                    }
                ),
                prompt: Yup.string().required(i18n.t("promptModal.validation.promptRequired")),
                maxTokens: Yup.number().min(1).max(4000).required(i18n.t("promptModal.validation.maxTokensRequired")),
                temperature: Yup.number().min(0).max(2).required(i18n.t("promptModal.validation.temperatureRequired")),
                maxMessages: Yup.number().min(1).max(100).required(i18n.t("promptModal.validation.maxMessagesRequired")),
                queueId: Yup.string().nullable().optional()
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [i18n.language, hasStoredApiKey]
    );

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const handleChangeProvider = (e, currentApiKeyValue) => {
        const newProvider = e.target.value;
        setSelectedProvider(newProvider);
        setApiValidationError(null);

        const keyEmpty = !(currentApiKeyValue && String(currentApiKeyValue).trim());
        if (newProvider === savedProvider && hasStoredApiKey && keyEmpty) {
            setApiValidated(true);
        } else if (newProvider !== savedProvider) {
            setApiValidated(false);
        }
    };

    useEffect(() => {
        // ✅ Resetear estado cuando se abre el modal sin promptId
        if (!open) {
            return;
        }

        const fetchPrompt = async () => {
            if (!promptId) {
                console.log("[PromptModal] No hay promptId, reseteando a valores iniciales");
                setPrompt(initialState);
                setMediaFiles([]);
                setSelectedWorkMode(initialState.workMode || "layers"); // ✅ Resetear workMode al valor inicial
                setSelectedProvider(initialState.provider || DEFAULT_AI_PROVIDER);
                setHasStoredApiKey(false);
                setSavedProvider(null);
                setApiValidated(false);
                setApiValidationError(null);
                return;
            }
            try {
                console.log("[PromptModal] Cargando prompt con ID:", promptId);
                const { data } = await getPrompt(promptId);
                console.log("[PromptModal] Datos recibidos del backend:", {
                    id: data.id,
                    name: data.name,
                    workMode: data.workMode,
                    workModeType: typeof data.workMode,
                    workModeValue: data.workMode,
                    allData: data
                });

                setPrompt(prevState => {
                    // ✅ CONVERTIR queueId null A STRING VACÍO PARA EVITAR WARNINGS
                    const processedData = {
                        ...data,
                        queueId: data.queueId || "", // Si es null, undefined o 0, usar string vacío
                        apiKey:
                            data.apiKey !== undefined && data.apiKey !== null
                                ? String(data.apiKey)
                                : "",
                    };
                    return { ...prevState, ...processedData };
                });
                setSelectedVoice(data.voice);
                setSelectedProvider(data.provider || DEFAULT_AI_PROVIDER);

                // ✅ Cargar workMode del backend - corregido: data ya es el prompt completo
                // Asegurarse de que workMode sea válido ('layers' o 'pdfs')
                // Normalizar a minúsculas para evitar problemas de case sensitivity
                const normalizedWorkMode = String(data.workMode || "layers").toLowerCase();
                const workModeValue = (normalizedWorkMode === 'layers' || normalizedWorkMode === 'pdfs')
                    ? normalizedWorkMode
                    : "layers";
                console.log("[PromptModal] Estableciendo workMode:", workModeValue, "desde data.workMode:", data.workMode, "normalized:", normalizedWorkMode);
                setSelectedWorkMode(workModeValue);

                // ✅ Guardar API Key y proveedor originales para comparar cambios
                setSavedProvider(data.provider || DEFAULT_AI_PROVIDER);

                const hasKey = Boolean(data.hasApiKey);
                setHasStoredApiKey(hasKey);

                /** Lista sigue ocultando clave; en edición llega valor en `apiKey`. */
                if (hasKey && data.provider) {
                    setApiValidated(true);
                    setApiValidationError(null);
                }

                // ✅ Cargar archivos multimedia
                await fetchMediaFiles(promptId);
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]); // Removido initialState de las dependencias

    const handleClose = () => {
        setPrompt(initialState);
        setSelectedVoice("texto"); // ✅ SIEMPRE "TEXTO" POR DEFECTO
        setSelectedProvider(DEFAULT_AI_PROVIDER);
        setSelectedWorkMode("layers"); // ✅ Resetear workMode al cerrar
        setMediaFiles([]);
        setUrlModalOpen(false);
        setIsDragging(null);
        setApiValidated(false);
        setApiValidationError(null);
        // ✅ Limpiar valores guardados al cerrar
        setHasStoredApiKey(false);
        setSavedProvider(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        onClose();
    };

    // ✅ Función para validar API Key y proveedor
    const handleValidateApi = async (currentApiKey) => {
        // ✅ CRÍTICO: Usar SOLO el valor pasado como parámetro (valor actual del formulario)
        // NO usar prompt.apiKey (estado guardado) - esto asegura que validamos lo que el usuario escribió
        if (!currentApiKey || !currentApiKey.trim()) {
            toast.error(i18n.t("promptModal.apiTest.enterApiKeyFirst"));
            return;
        }

        const apiKey = currentApiKey.trim();
        const provider = selectedProvider;

        if (!provider) {
            toast.error(i18n.t("promptModal.apiTest.selectProviderFirst"));
            return;
        }

        // ✅ Validar formato básico de API Key
        if (apiKey.length < 20) {
            toast.error(i18n.t("promptModal.apiTest.keyTooShortToast"));
            setApiValidated(false);
            setApiValidationError(i18n.t("promptModal.apiTest.keyTooShort"));
            return;
        }

        try {
            setValidatingApi(true);
            setApiValidationError(null);
            setApiValidated(false);

            console.log(`[ValidateAPI] Validando API Key (primeros 10 chars): ${apiKey.substring(0, 10)}..., Proveedor: ${provider}`);
            console.log(`[ValidateAPI] Longitud de API Key: ${apiKey.length} caracteres`);

            const { data } = await validatePromptApi({
                apiKey: apiKey, // ✅ Usar el valor exacto que el usuario ingresó
                provider: provider
            });

            if (data.success) {
                setApiValidated(true);
                setApiValidationError(null);
                toast.success(`✅ ${data.message || i18n.t("promptModal.apiTest.connectionSuccess")}`);
            } else {
                setApiValidated(false);
                setApiValidationError(data.message || i18n.t("promptModal.apiTest.validationFailed"));
                toast.error(data.message || i18n.t("promptModal.apiTest.validateError"));
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || i18n.t("promptModal.apiTest.validateError");
            setApiValidated(false);
            setApiValidationError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setValidatingApi(false);
        }
    };

    const handleSavePrompt = async values => {
        // ✅ VALIDAR API ANTES DE GUARDAR
        const trimmedKey = values.apiKey ? String(values.apiKey).trim() : "";
        const okPreserveStoredCredential =
            hasStoredApiKey &&
            !trimmedKey &&
            selectedProvider === savedProvider;

        if (!apiValidated && !okPreserveStoredCredential) {
            toast.error(
                hasStoredApiKey
                    ? i18n.t("promptModal.mustValidateBeforeSaveWithStored")
                    : i18n.t("promptModal.mustValidateBeforeSave")
            );
            return;
        }

        const promptData = { ...values, voice: selectedVoice, provider: selectedProvider, workMode: selectedWorkMode };
        console.log("[PromptModal] Guardando prompt con workMode:", selectedWorkMode, "promptData:", promptData);
        // ✅ ELIMINADA VALIDACIÓN OBLIGATORIA: Permitir prompts sin departamento
        // if (!values.queueId) {
        //     toastError("Informe o setor");
        //     return;
        // }
        try {
            let savedPromptId = promptId;
            let savedResp;
            if (promptId) {
                const { data } = await updatePrompt(promptId, promptData);
                savedResp = data;
            } else {
                const { data } = await createPrompt(promptData);
                savedResp = data;
                savedPromptId = data.id;
            }

            if (savedResp && typeof savedResp.hasApiKey !== "undefined")
                setHasStoredApiKey(!!savedResp.hasApiKey);
            setSavedProvider(selectedProvider);

            toast.success(i18n.t("promptModal.success"));

            // ✅ Si es un prompt nuevo, recargar archivos multimedia
            if (!promptId && savedPromptId) {
                await fetchMediaFiles(savedPromptId);
            }

            // ✅ AGREGAR CALLBACK PARA REFRESCAR LISTA
            if (onSave) {
                onSave();
            }
        } catch (err) {
            toastError(err);
        }
        // ✅ NO cerrar el modal si es un prompt nuevo (para permitir agregar multimedia)
        if (promptId) {
            handleClose();
        }
    };

    return {
        selectedVoice, setSelectedVoice,
        showApiKey, setShowApiKey,
        selectedProvider, setSelectedProvider,
        validatingApi, setValidatingApi,
        expandedPromptModalOpen, setExpandedPromptModalOpen,
        expandedPromptText, setExpandedPromptText,
        expandedPromptChanged, setExpandedPromptChanged,
        apiValidated, setApiValidated,
        apiValidationError, setApiValidationError,
        hasStoredApiKey, setHasStoredApiKey,
        savedProvider, setSavedProvider,
        selectedWorkMode, setSelectedWorkMode,
        prompt, setPrompt,
        validationSchema,
        handleToggleApiKey,
        handleChangeProvider,
        handleClose,
        handleValidateApi,
        handleSavePrompt,
    };
}
